/* eslint-disable no-underscore-dangle */
import {
  type $ZodArrayDef,
  type $ZodDefaultDef,
  type $ZodLiteralDef,
  type $ZodObjectDef,
  type $ZodOptionalDef,
  type $ZodStringFormatDef,
  type $ZodType,
  type $ZodTypes,
} from 'zod/v4/core'
import * as z from 'zod'
import type { FormField } from './form-field.js'
import { decodeMetadata } from './zod-meta.js'

const getFormType = (schema: $ZodType): FormField['type'] | 'optional' | 'default' => {
  const _schema = schema as $ZodTypes

  const def = _schema._zod.def

  switch (def.type) {
    case 'date':
      return 'datetime'
    case 'string':
      return 'text'
    case 'number':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'object':
      return 'object'
    case 'optional':
      return 'optional'
    case 'literal':
      return 'literal'
    case 'enum':
      return 'enum'
    case 'array':
      return 'list'
    case 'default':
      return 'default'
    default:
      throw new Error(`Field type ${def.type} not implemented`)
  }
}
/** @todo we can store metadata in `description` */

export const getFormFromSchema = (schema: $ZodType): FormField => {
  const _schema = schema as $ZodTypes

  /** @todo use meta directly - no need to encode metadata in the description string anymore */
  const registryMetadata = z.globalRegistry.get(schema)
  const metadata = decodeMetadata(registryMetadata?.description)
  const base = {
    required: true,
    metadata,
  } satisfies Partial<FormField>

  const values = _schema._zod.values ? ([..._schema._zod.values] as string[]) : []

  const type = getFormType(schema)

  switch (type) {
    case 'text':
      const _text = schema._zod.def as $ZodStringFormatDef

      const isDateTime = _text.format === 'datetime'

      return {
        ...base,
        type: isDateTime ? 'datetime' : 'text',
      }
    case 'boolean':
      return {
        ...base,
        type: 'boolean',
      }
    case 'enum':
      return {
        ...base,
        type: 'enum',
        values,
      }
    case 'datetime':
      return {
        ...base,
        type: 'datetime',
      }
    case 'literal':
      const _literal = schema._zod.def as $ZodLiteralDef<string>
      // @todo support multiple literals
      return {
        ...base,
        type: 'literal',
        value: _literal.values[0] as string,
      }
    case 'number':
      return {
        ...base,
        type: 'number',
      }
    case 'optional':
      const _optional = schema._zod.def as $ZodOptionalDef
      return {
        ...getFormFromSchema(_optional.innerType),
        required: false,
      }
    case 'default':
      const _default = schema._zod.def as $ZodDefaultDef
      return {
        ...getFormFromSchema(_default.innerType),
        required: false,
        defaultValue: _default.defaultValue,
      }

    case 'object':
      const _object = schema._zod.def as $ZodObjectDef
      const shape = _object.shape

      const childForm: Record<string, FormField> = {}
      for (const [name, val] of Object.entries(shape)) {
        childForm[name] = getFormFromSchema(val)
      }
      return {
        ...base,
        type: 'object',
        childForm,
      }
    case 'list':
      const _list = schema._zod.def as $ZodArrayDef
      /**
       * @todo add support for min / max array size
       */
      return {
        ...base,
        type: 'list',
        valueType: getFormFromSchema(_list.element),
      }

    default:
      throw new Error(`Field type ${schema._zod.def.type} not implemented`)
  }
}
